import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { fetchCitasByClienteId, fetchMisReservasPagina, Cita } from '../../../../services/citasService';
import { fetchPrecioPlanesServicios, PrecioPlanServicio } from '../../../../services/citasService';
import { fetchEspacioServicios, EspacioServicio } from '../../../../services/citasService';
import {
    listarSolicitudesReserva, SolicitudReserva,
} from '../../../../services/solicitudReservaService';
import { CalendarCheck2, Clock, MapPin, Banknote, CalendarX, CalendarDays, List } from 'lucide-react';
import { useWebSocketCitas } from '../../../../hooks/useWebSocket';
import { useNavigate } from 'react-router-dom';
import ClientReservationsCalendar from './ClientReservationsCalendar';
import { reservationStatus, canClientCancel, ReservationStatus } from '../../../../utils/reservationStatus';
import { cancelarReservaCliente } from '../../../../services/citasService';
import CancelReservationModal from './CancelReservationModal';

import { Pagination } from '../../../../components/paginationlanding/Pagination';

const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement | null>, boolean] {
    const ref = useRef<HTMLElement | null>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

type StatusKey = ReservationStatus;

// Un solo color por estado — todo lo demás deriva de él
const statusConfig: Record<StatusKey, {
    label: string;
    color: string;
    gradient: string;
    dot: boolean; // si el dot pulsa
}> = {
    'cancelled': { label: 'Cancelada', color: colors.secondary, gradient: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.secondary})`, dot: false },
    'upcoming':    { label: 'Próxima',    color: colors.primary,        gradient: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.primary})`,     dot: false },
    'in-progress': { label: 'En curso', color: colors.secondary,      gradient: `linear-gradient(145deg, ${colors.primary}, ${colors.secondary})`,        dot: true  },
    'completed':   { label: 'Completada', color: '#64748b',              gradient: `linear-gradient(145deg, #334155, #64748b)`,                             dot: false },
};

// ── Cita Card ──
function CitaCard({ cita, plan, espacio, index, now, userId, onCancel }: {
    now: number;
    userId?: number;
    onCancel: (cita: Cita) => void;
    cita: Cita;
    plan: PrecioPlanServicio | undefined;
    espacio: EspacioServicio | undefined;
    index: number;
}) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.06);
    const [hovered, setHovered] = useState(false);

    const formatDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('es-ES', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
    };

    const status = reservationStatus(cita, now);
    const sc = statusConfig[status];
    const isCompleted = status === 'completed';

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: isCompleted
                    ? (isDark ? 'rgba(20, 10, 38, 0.65)' : '#fafafa')
                    : (isDark ? 'rgba(22, 10, 43, 0.85)' : '#fff'),
                borderRadius: '24px',
                overflow: 'hidden',
                border: hovered && !isCompleted
                    ? `1.5px solid ${sc.color}60`
                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9'),
                boxShadow: hovered && !isCompleted
                    ? `0 20px 56px ${sc.color}25, 0 6px 24px rgba(0,0,0,0.2)`
                    : (isDark ? '0 8px 30px rgba(0,0,0,0.3)' : '0 3px 16px rgba(0,0,0,0.05)'),
                backdropFilter: 'blur(12px)',
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                transform: inView
                    ? (hovered && !isCompleted ? 'translateY(-5px)' : 'translateY(0)')
                    : 'translateY(24px)',
                opacity: inView ? (isCompleted ? 0.75 : 1) : 0,
                transitionDelay: `${index * 0.06}s`,
                filter: isCompleted ? 'saturate(0.6)' : 'none',
            }}
        >
            {/* Barra de acento superior — igual que AboutusProjects */}
            <div style={{
                height: '4px',
                background: hovered && !isCompleted
                    ? sc.gradient.replace('linear-gradient(145deg,', 'linear-gradient(90deg,')
                    : `linear-gradient(90deg, ${sc.color}60, ${sc.color}20)`,
                transition: 'background 0.35s',
            }} />

            {/* Header de estado — limpio, un solo color */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px',
                borderBottom: `1px solid ${sc.color}15`,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Dot */}
                    <span style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: sc.color, display: 'inline-block', flexShrink: 0,
                        boxShadow: sc.dot ? `0 0 0 3px ${sc.color}25` : 'none',
                        animation: sc.dot ? 'pulseCita 2s infinite' : 'none',
                    }} />
                    <span style={{
                        fontSize: '0.75rem', fontWeight: '700',
                        color: sc.color,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}>
                        {sc.label}
                    </span>
                </div>
                <span style={{
                    fontSize: '0.7rem', fontWeight: '700', color: isDark ? '#94a3b8' : '#94a3b8',
                    background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#f8fafc',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #f1f5f9',
                    borderRadius: '6px', padding: '2px 9px',
                }}>
                    #{cita.id}
                </span>
            </div>

            {cita.es_asociado && <p style={{ margin: '10px 20px 0', color: colors.secondary, fontSize: '.72rem', fontWeight: 700 }}>Reserva compartida · Asociado</p>}

            {/* Cuerpo */}
            <div style={{ padding: '20px' }}>

                {/* Fecha y hora — estilo igual a ProjectCard: icono coloreado + texto grande */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '18px' }}>
                    <div style={{
                        width: '46px', height: '46px', borderRadius: '14px', flexShrink: 0,
                        background: `${sc.color}12`,
                        border: `1.5px solid ${sc.color}25`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.3s, border-color 0.3s',
                        ...(hovered && !isCompleted ? {
                            background: sc.gradient,
                        } : {}),
                    }}>
                        <CalendarCheck2
                            size={20}
                            color={hovered && !isCompleted ? '#fff' : sc.color}
                            strokeWidth={2}
                            style={{ transition: 'color 0.3s' }}
                        />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{
                            fontSize: '0.97rem', fontWeight: '800',
                            color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.2', marginBottom: '5px', textTransform: 'capitalize',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {cita.fecha_inicio ? formatDate(cita.fecha_inicio) : 'Fecha no disponible'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Clock size={13} strokeWidth={2} color={isDark ? '#cbd5e1' : '#94a3b8'} />
                            <span style={{ fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#64748b' }}>
                                {cita.hora_inicio} — {cita.hora_fin}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Divisor con color de acento */}
                <div style={{ height: '1.5px', background: `linear-gradient(90deg, ${sc.color}20, transparent)`, marginBottom: '16px', borderRadius: '2px' }} />

                {/* Tags info — igual que los tags de ProjectCard */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {/* Servicio */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        background: `${sc.color}0e`,
                        border: `1.5px solid ${sc.color}22`,
                        borderRadius: '8px', padding: '5px 12px',
                        fontSize: '0.78rem', fontWeight: '700', color: sc.color,
                    }}>
                        <MapPin size={12} strokeWidth={2.5} />
                        {plan ? `${plan.servicio_nombre}` : 'Sin servicio'}
                    </span>

                    {/* Plan */}
                    {plan?.plan_nombre && (
                        <span style={{
                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#f8fafc',
                            border: isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #f1f5f9',
                            borderRadius: '8px', padding: '5px 12px',
                            fontSize: '0.78rem', fontWeight: '600',
                            color: isDark ? '#cbd5e1' : '#64748b',
                        }}>
                            {plan.plan_nombre}
                        </span>
                    )}

                    {/* Total */}
                    {!cita.es_asociado && <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        background: `${colors.accentDark}0e`,
                        border: `1.5px solid ${colors.accentDark}22`,
                        borderRadius: '8px', padding: '5px 12px',
                        fontSize: '0.78rem', fontWeight: '700', color: colors.accentDark,
                    }}>
                        <Banknote size={12} strokeWidth={2.5} />
                        {cita.total} Bs.
                    </span>}
                </div>

                {/* Espacio — badge discreto */}
                {espacio && (
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                        background: isDark ? 'rgba(193, 52, 139, 0.15)' : `${colors.primary}08`,
                        border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : `1px solid ${colors.primary}18`,
                        borderRadius: '8px', padding: '6px 12px',
                        marginBottom: '18px', width: '100%',
                    }}>
                        <span style={{ fontSize: '0.68rem', fontWeight: '800', color: isDark ? '#ffd97c' : colors.primary, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
                            📍 Espacio
                        </span>
                        <span style={{ width: '1px', height: '12px', background: `${colors.primary}20`, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: isDark ? '#f1f5f9' : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {espacio.espacio_nombre}
                        </span>
                    </div>
                )}

                {/* Footer acciones */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    paddingTop: '14px',
                    borderTop: isDark ? '1.5px solid rgba(255, 255, 255, 0.06)' : '1.5px solid #f8fafc',
                }}>
                    <a
                        href={`/mis-citas/${cita.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', borderRadius: '10px',
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            color: '#ffffff', fontSize: '0.78rem', fontWeight: '800',
                            textDecoration: 'none',
                            boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                            transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 18px rgba(193, 52, 139, 0.45)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 14px rgba(97, 37, 123, 0.35)';
                        }}
                    >
                        Ver detalles →
                    </a>

                    {canClientCancel(cita, userId) && (
                        <button onClick={() => onCancel(cita)} style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '0.78rem', fontWeight: '600', color: '#cbd5e1',
                            fontFamily: "'DM Sans', sans-serif",
                            transition: 'color 0.2s', padding: '4px 0',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.color = colors.primary; }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; }}
                        >
                            Cancelar reserva
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Hero de sección ──
function PageHeader({ total }: { total: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(() => setForced(true), 80); return () => clearTimeout(t); }, []);
    const visible = inView || forced;

    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} style={{ marginBottom: '40px' }}>
            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                <a href="/midashboard" style={{
                    fontSize: '0.78rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#94a3b8', textDecoration: 'none', transition: 'color 0.2s',
                }}
                    onMouseEnter={e => { e.currentTarget.style.color = colors.secondary; }}
                    onMouseLeave={e => { e.currentTarget.style.color = isDark ? '#cbd5e1' : '#94a3b8'; }}
                >Dashboard</a>
                <span style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#e2e8f0', fontSize: '0.8rem' }}>/</span>
                <span style={{ fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#ffd97c' : colors.primary }}>Mis Reservas</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                    <h1
                        className={`fade-up-citas ${visible ? 'in' : ''}`}
                        style={{
                            fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)',
                            fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                            lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '8px',
                        }}
                    >
                        Mis <span style={{ color: isDark ? '#ffd97c' : colors.primary }}>Reservas</span>
                    </h1>
                    <p className={`fade-up-citas d1 ${visible ? 'in' : ''}`}
                        style={{ fontSize: '0.93rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500' }}>
                        Gestiona y consulta todas tus citas agendadas en Co Marca.
                    </p>
                </div>

                {/* Badge contador */}
                <div className={`fade-up-citas d2 ${visible ? 'in' : ''}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: isDark ? 'rgba(193, 52, 139, 0.2)' : `${colors.primary}10`,
                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : `1.5px solid ${colors.primary}22`,
                    borderRadius: '100px', padding: '8px 20px',
                    fontSize: '0.82rem', fontWeight: '700', color: isDark ? '#ffd97c' : colors.primaryDark,
                    flexShrink: 0,
                }}>
                    <span style={{
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: colors.secondary, display: 'inline-block',
                        animation: 'pulseCita 2s infinite',
                    }} />
                    {total} {total === 1 ? 'cita reservada' : 'citas reservadas'}
                </div>
            </div>

            {/* Barra de marca */}
            <div className={`fade-up-citas d2 ${visible ? 'in' : ''}`}
                style={{ display: 'flex', gap: '5px', marginTop: '20px' }}>
                {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                    <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                ))}
            </div>
        </div>
    );
}

// ── Main ──
interface MisCitasClientePageProps { mode?: 'reservas' | 'solicitudes'; }

const MisCitasClientePage = ({ mode = 'reservas' }: MisCitasClientePageProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [now, setNow] = useState(Date.now());
    const [cancelTarget, setCancelTarget] = useState<Cita | null>(null);
    useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 30000); return () => window.clearInterval(timer); }, []);
    const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
    const [pagina, setPagina] = useState(1);
    const [total, setTotal] = useState(0);
    const [recarga, setRecarga] = useState(0);
    const [errorCarga, setErrorCarga] = useState('');
    const [citas, setCitas] = useState<Cita[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [precioPlanes, setPrecioPlanes] = useState<PrecioPlanServicio[]>([]);
    const [espacios, setEspacios] = useState<EspacioServicio[]>([]);
    const [solicitudes, setSolicitudes] = useState<SolicitudReserva[]>([]);

    useEffect(() => {
        if (!user?.id || mode !== 'reservas') return;
        let active = true;
        Promise.all([fetchPrecioPlanesServicios(), fetchEspacioServicios()])
            .then(([planes, espaciosDisponibles]) => {
                if (active) { setPrecioPlanes(planes); setEspacios(espaciosDisponibles); }
            }).catch(error => console.error('Error cargando catálogos:', error));
        return () => { active = false; };
    }, [user?.id, mode]);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setErrorCarga('');
        const load = async () => {
            if (!user?.id) { if (active) setIsLoading(false); return; }
            try {
                if (mode === 'solicitudes') {
                    const result = await listarSolicitudesReserva();
                    if (active) setSolicitudes([...result].sort((a, b) => b.id - a.id));
                } else if (viewMode === 'list') {
                    const result = await fetchMisReservasPagina(pagina);
                    if (active) { setCitas(result.results); setTotal(result.count); }
                } else {
                    const result = await fetchCitasByClienteId(user.id);
                    if (active) { setCitas(result); setTotal(result.length); }
                }
            } catch {
                if (active) { setCitas([]); setErrorCarga('No se pudieron cargar tus reservas. Intenta nuevamente.'); }
            } finally {
                if (active) setIsLoading(false);
            }
        };
        void load();
        return () => { active = false; };
    }, [user?.id, mode, viewMode, pagina, recarga]);

    // ── WebSocket: tiempo real ──────────────────────────────
    const handleCitaEvent = useCallback((action: string, citaData: any) => {
        // La actualización es específica para el participante autenticado.
        if (citaData.cliente !== user?.id && !citaData.es_asociado &&
            !citaData.clientes_asociados?.includes(user?.id)) return;

        if (['create', 'update', 'delete'].includes(action)) {
            setRecarga(value => value + 1);
        }
    }, [user?.id]);

    useWebSocketCitas(handleCitaEvent);

    const { isDark } = useTheme();

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", minHeight: '100vh', background: 'transparent' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulseCita {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(193,52,139,0.35); }
                    50%      { box-shadow: 0 0 0 5px rgba(193,52,139,0); }
                }
                .fade-up-citas {
                    opacity: 0; transform: translateY(20px);
                    transition: opacity 0.65s ease, transform 0.65s cubic-bezier(.16,1,.3,1);
                }
                .fade-up-citas.in  { opacity: 1; transform: translateY(0); }
                .fade-up-citas.d1  { transition-delay: 0.08s; }
                .fade-up-citas.d2  { transition-delay: 0.16s; }
                .reservations-overview {
                    display: grid; grid-template-columns: minmax(0, 1fr) minmax(380px, .9fr);
                    gap: 24px; align-items: start; margin-bottom: 32px;
                }
                .reservations-calendar { position: sticky; top: 20px; }
                @media (max-width: 900px) {
                    .reservations-overview { grid-template-columns: 1fr; }
                    .reservations-calendar { position: static; order: -1; }
                }
            `}</style>

            {cancelTarget && <CancelReservationModal reservation={cancelTarget} onClose={() => setCancelTarget(null)} onConfirm={async motivo => {
                const updated = await cancelarReservaCliente(cancelTarget.id, motivo);
                setCitas(current => current.map(c => c.id === updated.id ? updated : c));
                setCancelTarget(null);
            }} />}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(24px, 4vw, 44px) clamp(16px, 4vw, 32px)' }}>

                <PageHeader total={mode === 'reservas' ? total : solicitudes.length} />

                {errorCarga && <div role="alert">{errorCarga} <button onClick={() => setRecarga(value => value + 1)}>Reintentar</button></div>}

                {!isLoading && mode === 'solicitudes' && (
                    <section aria-labelledby="solicitudes-title">
                        <h2 id="solicitudes-title" style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '12px' }}>
                            Solicitudes de reserva
                        </h2>
                        {solicitudes.length ? (
                            <div style={{ display: 'grid', gap: '12px' }}>
                                {solicitudes.map(solicitud => (
                                    <article key={solicitud.id} style={{
                                        background: '#fff', border: `1.5px solid ${colors.accentDark}30`,
                                        borderRadius: '18px', padding: '18px 20px',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        gap: '16px', flexWrap: 'wrap',
                                    }}>
                                        <div>
                                            <div style={{ color: colors.accentDark, fontWeight: '800', fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                {solicitud.estado === 'pendiente' ? 'Pendiente' : solicitud.estado}
                                            </div>
                                            <div style={{ fontWeight: '800', color: '#0f172a', marginTop: '4px' }}>{solicitud.servicio_nombre || 'Solicitud de reserva'}</div>
                                            <div style={{ color: '#64748b', fontSize: '0.84rem', marginTop: '3px' }}>{solicitud.fecha_inicio} · {solicitud.hora_inicio} · Código {solicitud.codigo}</div>
                                        </div>
                                        {solicitud.whatsapp_url && solicitud.estado === 'pendiente' && (
                                            <a href={solicitud.whatsapp_url} target="_blank" rel="noreferrer" style={{
                                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`, color: '#fff', textDecoration: 'none',
                                                borderRadius: '12px', padding: '10px 16px', fontWeight: '800', fontSize: '0.82rem',
                                            }}>Continuar por WhatsApp</a>
                                        )}
                                    </article>
                                ))}
                            </div>
                        ) : <p style={{ color: '#64748b' }}>No tienes solicitudes de reserva pendientes.</p>}
                    </section>
                )}

                {/* Loading */}
                {isLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px', gap: '16px' }}>
                        <div style={{
                            width: '44px', height: '44px', borderRadius: '50%',
                            border: `3px solid ${colors.primary}22`,
                            borderTop: `3px solid ${colors.primary}`,
                            animation: 'spin 0.75s linear infinite',
                        }} />
                        <span style={{ fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '600' }}>Cargando tus reservas...</span>
                    </div>
                )}

                {/* Empty */}
                {!isLoading && !errorCarga && mode === 'reservas' && citas.length === 0 && (
                    <div style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.85)' : '#fff',
                        borderRadius: '28px',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.25)' : '1.5px solid #f1f5f9',
                        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 4px 28px rgba(0,0,0,0.05)',
                        backdropFilter: 'blur(12px)',
                        padding: 'clamp(40px, 8vw, 80px) 32px',
                        textAlign: 'center',
                    }}>
                        <div style={{
                            width: '76px', height: '76px', borderRadius: '22px',
                            background: `${colors.primary}0e`,
                            border: `1.5px solid ${colors.primary}1a`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                        }}>
                            <CalendarX size={34} color={colors.primary} strokeWidth={1.5} />
                        </div>
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '10px' }}>
                            No tienes citas reservadas
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: isDark ? '#cbd5e1' : '#64748b', maxWidth: '340px', margin: '0 auto 24px', lineHeight: '1.7' }}>
                            Actualmente no tienes citas programadas. Reserva una nueva cita para comenzar.
                        </p>
                        <a href="/reservaya" style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                            color: '#fff', borderRadius: '12px', padding: '12px 28px',
                            fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
                            boxShadow: `0 6px 20px ${colors.primary}28`,
                            transition: 'transform 0.2s',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Reservar nueva cita →
                        </a>
                    </div>
                )}

                {!isLoading && mode === 'reservas' && citas.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
                        <button onClick={() => setViewMode('calendar')} aria-pressed={viewMode === 'calendar'} style={{ padding: '9px 13px', borderRadius: '10px', border: '1px solid #e2e8f0', background: viewMode === 'calendar' ? colors.secondary : '#fff', color: viewMode === 'calendar' ? '#fff' : '#64748b', display: 'inline-flex', gap: '6px', alignItems: 'center', fontWeight: 800, cursor: 'pointer' }}><CalendarDays size={15} /> Calendario</button>
                        <button onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'} style={{ padding: '9px 13px', borderRadius: '10px', border: '1px solid #e2e8f0', background: viewMode === 'list' ? colors.secondary : '#fff', color: viewMode === 'list' ? '#fff' : '#64748b', display: 'inline-flex', gap: '6px', alignItems: 'center', fontWeight: 800, cursor: 'pointer' }}><List size={15} /> Lista</button>
                    </div>
                )}

                {!isLoading && mode === 'reservas' && citas.length > 0 && viewMode === 'calendar' && (
                    <ClientReservationsCalendar reservations={citas} onOpenReservation={id => navigate(`/mis-citas/${id}`)} />
                )}

                {/* Grid */}
                {!isLoading && mode === 'reservas' && citas.length > 0 && viewMode === 'list' && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                        gap: '18px',
                    }}>
                        {citas.map((cita, idx) => (
                            <CitaCard
                                key={cita.id}
                                cita={cita}
                                plan={precioPlanes.find(p => p.id === cita.precio_plan_servicio)}
                                espacio={espacios.find(e => e.id === cita.espacio_servicio)}
                                index={idx}
                                now={now}
                                userId={user?.id}
                                onCancel={setCancelTarget}
                            />
                        ))}
                    </div>
                )}
                {mode === 'reservas' && viewMode === 'list' && !errorCarga && <Pagination currentPage={pagina} totalPages={Math.ceil(total / 10)} onPageChange={setPagina} disabled={isLoading} />}
            </div>
        </div>
    );
};

export const SolicitudesReservaClientePage = () => <MisCitasClientePage mode="solicitudes" />;

export default MisCitasClientePage;



